<template>
    <div class="admin-feedback-page">
        <div class="page-header">
            <h2>反馈管理</h2>
            <p class="subtitle">查看用户反馈并进行回复沟通</p>
        </div>

        <el-table :data="feedbacks" v-loading="loading" border stripe style="width: 100%"
            :row-class-name="rowClassName">
            <el-table-column prop="id" label="ID" width="60" />
            <el-table-column prop="username" label="用户" width="100" />
            <el-table-column prop="type" label="类型" width="100">
                <template #default="{ row }">
                    <el-tag size="small" :type="row.type === '申诉' ? 'danger' : ''">{{ row.type }}</el-tag>
                </template>
            </el-table-column>
            <el-table-column prop="title" label="标题" min-width="200" show-overflow-tooltip />
            <el-table-column prop="questionId" label="题目ID" width="80">
                <template #default="{ row }">
                    <span v-if="row.questionId">#{{ row.questionId }}</span>
                    <span v-else style="color:#ccc;">-</span>
                </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="110">
                <template #default="{ row }">
                    <div style="display:flex; align-items:center; gap:6px;">
                        <span :class="['status-dot', row.status]"></span>
                        <el-tag :type="statusType(row.status)" size="small" effect="plain">{{ statusLabel(row.status)
                            }}</el-tag>
                    </div>
                </template>
            </el-table-column>
            <el-table-column label="更新时间" width="140">
                <template #default="{ row }">
                    {{ formatTime(row.updatedAt || row.createdAt) }}
                </template>
            </el-table-column>
            <el-table-column label="操作" width="100" fixed="right">
                <template #default="{ row }">
                    <el-button type="primary" size="small" @click="openChat(row)">查看/回复</el-button>
                </template>
            </el-table-column>
        </el-table>

        <!-- Chat Dialog -->
        <el-dialog v-model="showChatDialog" :title="chatTitle" width="650px" append-to-body destroy-on-close>
            <div v-loading="loadingChat" class="chat-container" ref="chatContainerRef">
                <div v-for="msg in chatMessages" :key="msg.id" class="chat-bubble-row"
                    :class="{ 'is-admin': msg.senderRole === 'admin' }">
                    <div class="chat-avatar">
                        <el-avatar :size="32"
                            :style="msg.senderRole === 'admin' ? 'background:#409eff' : 'background:#67c23a'">
                            {{ msg.senderRole === 'admin' ? '管' : '用' }}
                        </el-avatar>
                    </div>
                    <div class="chat-bubble" :class="msg.senderRole === 'admin' ? 'admin-bubble' : 'user-bubble'">
                        <div class="bubble-name">{{ msg.senderName || (msg.senderRole === 'admin' ? '管理员' : '用户') }}
                            <span class="bubble-time">{{ formatTime(msg.createdAt) }}</span>
                        </div>
                        <div class="bubble-content">{{ msg.content }}</div>
                        <div v-if="msg.senderRole === 'admin'" class="read-receipt">
                            <span v-if="msg.isRead" class="receipt-read" title="已读">✓✓</span>
                            <span v-else class="receipt-unread" title="未读">✓</span>
                        </div>
                    </div>
                </div>
            </div>

            <template #footer>
                <div class="chat-footer">
                    <!-- Quick template dropdown -->
                    <div class="template-bar">
                        <el-dropdown @command="handleTemplateSelect" trigger="click">
                            <el-button size="small" type="info" plain>
                                <el-icon style="margin-right:4px;">
                                    <Document />
                                </el-icon>
                                快速模板 <el-icon class="el-icon--right">
                                    <ArrowDown />
                                </el-icon>
                            </el-button>
                            <template #dropdown>
                                <el-dropdown-menu>
                                    <el-dropdown-item v-for="(t, idx) in replyTemplates" :key="idx"
                                        :command="t.content">
                                        <el-tag
                                            :type="t.category === 'reject' ? 'danger' : (t.category === 'approve' ? 'success' : 'info')"
                                            size="small" style="margin-right:6px;">
                                            {{ t.category === 'reject' ? '驳回' : (t.category === 'approve' ? '通过' : '通用')
                                            }}
                                        </el-tag>
                                        {{ t.label }}
                                    </el-dropdown-item>
                                </el-dropdown-menu>
                            </template>
                        </el-dropdown>

                        <!-- Appeal action buttons (only for 申诉 type, not yet resolved/rejected) -->
                        <div v-if="isAppealFeedback && !isAppealClosed" class="appeal-actions">
                            <el-button type="danger" size="small" plain @click="handleRejectAppeal"
                                :loading="resolving">
                                <el-icon style="margin-right:4px;">
                                    <CircleClose />
                                </el-icon> 驳回申诉
                            </el-button>
                            <el-button type="success" size="small" @click="handleApproveAppeal" :loading="resolving">
                                <el-icon style="margin-right:4px;">
                                    <CircleCheck />
                                </el-icon> 通过解封
                            </el-button>
                        </div>
                        <el-tag v-else-if="isAppealFeedback && isAppealClosed"
                            :type="currentFeedbackStatus === 'resolved' ? 'success' : 'danger'" effect="dark"
                            size="small">
                            {{ currentFeedbackStatus === 'resolved' ? '已解封' : '已驳回' }}
                        </el-tag>
                    </div>

                    <el-input v-model="replyContent" type="textarea" :rows="2" placeholder="输入回复内容..."
                        @keyup.ctrl.enter="sendReply" />
                    <div class="chat-footer-actions">
                        <el-button type="primary" :disabled="!replyContent.trim()" :loading="sending"
                            @click="sendReply">
                            回复 <span style="font-size:11px; opacity:0.7; margin-left:4px;">(Ctrl+Enter)</span>
                        </el-button>
                    </div>
                </div>
            </template>
        </el-dialog>

        <!-- Reject reason dialog -->
        <el-dialog v-model="showRejectDialog" title="驳回申诉" width="450px" append-to-body>
            <el-form label-position="top">
                <el-form-item label="驳回原因">
                    <el-select v-model="rejectTemplate" placeholder="选择模板（可选）" clearable
                        style="width:100%; margin-bottom:8px;" @change="onRejectTemplateChange">
                        <el-option v-for="t in rejectTemplates" :key="t.label" :label="t.label" :value="t.content" />
                    </el-select>
                    <el-input v-model="rejectReason" type="textarea" :rows="3" placeholder="输入驳回原因，或从上方模板选择..." />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="showRejectDialog = false">取消</el-button>
                <el-button type="danger" :loading="resolving" @click="confirmRejectAppeal">确认驳回</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Document, ArrowDown, CircleClose, CircleCheck } from '@element-plus/icons-vue'
import api from '@/utils/api'

const loading = ref(false)
const feedbacks = ref([])

// Chat
const showChatDialog = ref(false)
const loadingChat = ref(false)
const chatMessages = ref([])
const chatTitle = ref('')
const currentFeedbackId = ref(null)
const currentFeedbackType = ref('')
const currentFeedbackStatus = ref('')
const replyContent = ref('')
const sending = ref(false)
const chatContainerRef = ref(null)

// Templates
const replyTemplates = ref([])

// Appeal resolve
const resolving = ref(false)
const showRejectDialog = ref(false)
const rejectReason = ref('')
const rejectTemplate = ref('')

const isAppealFeedback = computed(() =>
    currentFeedbackType.value === '申诉' || currentFeedbackType.value === 'Account Appeal'
)
const isAppealClosed = computed(() =>
    currentFeedbackStatus.value === 'resolved' || currentFeedbackStatus.value === 'rejected'
)
const rejectTemplates = computed(() =>
    replyTemplates.value.filter(t => t.category === 'reject')
)

const fetchFeedbacks = async () => {
    loading.value = true
    try {
        const res = await api.get('/api/admin/feedbacks')
        feedbacks.value = res.data.feedbacks || []
    } catch (err) {
        ElMessage.error('加载反馈列表失败')
    } finally {
        loading.value = false
    }
}

const fetchTemplates = async () => {
    try {
        const res = await api.get('/api/admin/reply-templates')
        replyTemplates.value = res.data.templates || []
    } catch (err) {
        // Silent fail, templates are optional
    }
}

const openChat = async (fb) => {
    currentFeedbackId.value = fb.id
    currentFeedbackType.value = fb.type
    currentFeedbackStatus.value = fb.status
    chatTitle.value = `[${fb.username}] ${fb.type} — ${fb.title || ''}`
    showChatDialog.value = true
    loadingChat.value = true
    chatMessages.value = []
    replyContent.value = ''

    try {
        const res = await api.get(`/api/feedbacks/${fb.id}`)
        chatMessages.value = res.data.feedback.messages || []
        await nextTick()
        scrollToBottom()
    } catch (err) {
        ElMessage.error('加载对话失败')
    } finally {
        loadingChat.value = false
    }
}

const handleTemplateSelect = (content) => {
    replyContent.value = content
}

const sendReply = async () => {
    if (!replyContent.value.trim()) return
    sending.value = true
    try {
        const res = await api.post(`/api/feedbacks/${currentFeedbackId.value}/messages`, {
            content: replyContent.value
        })
        chatMessages.value.push(res.data.msg)
        replyContent.value = ''
        const fb = feedbacks.value.find(f => f.id === currentFeedbackId.value)
        if (fb) fb.status = 'replied'
        currentFeedbackStatus.value = 'replied'
        await nextTick()
        scrollToBottom()
    } catch (err) {
        ElMessage.error('回复失败')
    } finally {
        sending.value = false
    }
}

const handleApproveAppeal = async () => {
    try {
        await ElMessageBox.confirm(
            '确认通过该用户的申诉并解除封禁？解封后用户将恢复所有权限。',
            '通过解封',
            { confirmButtonText: '确认解封', cancelButtonText: '取消', type: 'success' }
        )
    } catch { return }

    resolving.value = true
    try {
        await api.put(`/api/admin/feedbacks/${currentFeedbackId.value}/resolve-appeal`, {
            action: 'approve',
            reason: ''
        })
        ElMessage.success('申诉已通过，用户已解封')
        currentFeedbackStatus.value = 'resolved'
        const fb = feedbacks.value.find(f => f.id === currentFeedbackId.value)
        if (fb) fb.status = 'resolved'
        // Reload chat to see new system message
        const res = await api.get(`/api/feedbacks/${currentFeedbackId.value}`)
        chatMessages.value = res.data.feedback.messages || []
        await nextTick()
        scrollToBottom()
    } catch (err) {
        ElMessage.error(err.response?.data?.error || '操作失败')
    } finally {
        resolving.value = false
    }
}

const handleRejectAppeal = () => {
    rejectReason.value = ''
    rejectTemplate.value = ''
    showRejectDialog.value = true
}

const onRejectTemplateChange = (val) => {
    if (val) rejectReason.value = val
}

const confirmRejectAppeal = async () => {
    resolving.value = true
    try {
        await api.put(`/api/admin/feedbacks/${currentFeedbackId.value}/resolve-appeal`, {
            action: 'reject',
            reason: rejectReason.value
        })
        ElMessage.success('申诉已驳回')
        showRejectDialog.value = false
        currentFeedbackStatus.value = 'rejected'
        const fb = feedbacks.value.find(f => f.id === currentFeedbackId.value)
        if (fb) fb.status = 'rejected'
        // Reload chat
        const res = await api.get(`/api/feedbacks/${currentFeedbackId.value}`)
        chatMessages.value = res.data.feedback.messages || []
        await nextTick()
        scrollToBottom()
    } catch (err) {
        ElMessage.error(err.response?.data?.error || '操作失败')
    } finally {
        resolving.value = false
    }
}

const scrollToBottom = () => {
    if (chatContainerRef.value) {
        chatContainerRef.value.scrollTop = chatContainerRef.value.scrollHeight
    }
}

const rowClassName = ({ row }) => {
    return row.status === 'pending' ? 'row-pending' : ''
}

const statusType = (s) => {
    if (s === 'pending') return 'warning'
    if (s === 'replied') return 'success'
    if (s === 'rejected') return 'danger'
    if (s === 'resolved') return 'info'
    return 'info'
}
const statusLabel = (s) => {
    if (s === 'pending') return '待处理'
    if (s === 'replied') return '已回复'
    if (s === 'rejected') return '已驳回'
    if (s === 'resolved') return '已解决'
    return s
}

const formatTime = (ts) => {
    if (!ts) return ''
    const d = new Date(ts * 1000)
    const now = new Date()
    const isToday = d.toDateString() === now.toDateString()
    const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    if (isToday) return `今天 ${time}`
    return `${d.getMonth() + 1}/${d.getDate()} ${time}`
}

onMounted(() => {
    fetchFeedbacks()
    fetchTemplates()
})
</script>

<style scoped>
.admin-feedback-page {
    padding: 24px;
}

.page-header {
    margin-bottom: 24px;
}

.page-header h2 {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
}

.subtitle {
    font-size: 14px;
    color: #94a3b8;
    margin-top: 4px;
}

:deep(.row-pending) {
    background-color: #fffbe6 !important;
}

/* Chat styles */
.chat-container {
    max-height: 400px;
    overflow-y: auto;
    padding: 16px;
    background: #f5f7fa;
    border-radius: 12px;
    margin-bottom: 12px;
}

.chat-bubble-row {
    display: flex;
    margin-bottom: 16px;
    gap: 10px;
}

.chat-bubble-row.is-admin {
    flex-direction: row-reverse;
}

.chat-avatar {
    flex-shrink: 0;
}

.chat-bubble {
    max-width: 70%;
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 14px;
    line-height: 1.5;
}

.admin-bubble {
    background: #e1f3d8;
    border-radius: 12px 2px 12px 12px;
}

.user-bubble {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 2px 12px 12px 12px;
}

.bubble-name {
    font-size: 12px;
    color: #999;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.bubble-time {
    font-size: 11px;
    color: #c0c4cc;
}

.bubble-content {
    color: #303133;
    white-space: pre-wrap;
    word-break: break-word;
}

.chat-footer {
    width: 100%;
}

.template-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.appeal-actions {
    display: flex;
    gap: 8px;
    margin-left: auto;
}

.chat-footer-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
}

/* Status dot indicators */
.status-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.status-dot.pending {
    background: #e6a23c;
    box-shadow: 0 0 6px rgba(230, 162, 60, 0.5);
    animation: pulse-dot 2s infinite;
}

.status-dot.replied {
    background: #67c23a;
}

.status-dot.rejected {
    background: #f56c6c;
}

.status-dot.resolved {
    background: #909399;
}

@keyframes pulse-dot {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.4;
    }
}

/* Read receipt indicators */
.read-receipt {
    text-align: right;
    margin-top: 4px;
    font-size: 13px;
    line-height: 1;
}

.receipt-read {
    color: #409eff;
    font-weight: 700;
    letter-spacing: -2px;
}

.receipt-unread {
    color: #c0c4cc;
    font-weight: 600;
}
</style>
